import { ArrowDownRightIcon, ArrowUpRightIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';

import { deltaRatio, formatDelta } from '../../utils/helpers/aiSpendFormat';

const TONES = {
  slate: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  violet: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
};

/**
 * A KPI tile.
 *
 * `value` is what gets displayed and may be anything renderable. The trend is
 * computed from `rawValue` / `previousValue`, which must be numbers — the card
 * never reads `value` for arithmetic. Deriving a delta from a rendered string
 * is how a dashboard ends up reporting "−99%" because parseFloat('1,234')
 * returned 1.
 */
export default function StatCard({
  icon: Icon,
  label,
  value,
  rawValue,
  previousValue,
  deltaDirection = 'up-is-bad',
  detail,
  tone = 'slate',
  loading = false,
  to,
}) {
  if (process.env.NODE_ENV !== 'production') {
    if (typeof rawValue === 'string' || typeof previousValue === 'string') {
      // eslint-disable-next-line no-console
      console.error(
        `StatCard "${label}": rawValue/previousValue must be numbers. Pass raw ` +
          'numbers and format only via the `value` prop.',
      );
    }
  }

  const ratio = deltaRatio(rawValue, previousValue);
  const delta = formatDelta(ratio);
  const isBad = ratio !== null && (deltaDirection === 'up-is-bad' ? ratio > 0 : ratio < 0);
  const DeltaIcon = ratio !== null && ratio >= 0 ? ArrowUpRightIcon : ArrowDownRightIcon;

  const body = (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
          {loading ? (
            <div className="mt-2 h-7 w-24 animate-pulse rounded bg-slate-100 dark:bg-slate-700" />
          ) : (
            <p className="mt-1 truncate text-2xl font-bold text-slate-900 dark:text-white">
              {value}
            </p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            {delta && (
              <span
                className={`inline-flex items-center gap-0.5 font-semibold ${
                  isBad
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-emerald-600 dark:text-emerald-400'
                }`}
              >
                <DeltaIcon className="h-3 w-3" />
                {delta}
              </span>
            )}
            {detail && <span className="text-slate-500 dark:text-slate-400">{detail}</span>}
          </div>
        </div>
        {Icon && (
          <span className={`shrink-0 rounded-xl p-2.5 ${TONES[tone] || TONES.slate}`}>
            <Icon className="h-5 w-5" />
          </span>
        )}
      </div>
    </div>
  );

  return to ? (
    <Link to={to} className="block focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-2xl">
      {body}
    </Link>
  ) : (
    body
  );
}
